import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Typography,
  Fab,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  useMediaQuery
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useTheme } from '../../context/ThemeContext';
import { logDev } from '../../utils/logger';

const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    rating: 0,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { themeMode, jamaicanColors } = useTheme();

  const categories = [
    'User Experience',
    'Missing Features',
    'Partnership Ideas',
    'Technical Issues',
    'Other'
  ];

  const handleOpen = () => setOpen(true);

  // Listen for custom event to open feedback widget
  useEffect(() => {
    const handleOpenEvent = () => {
      setOpen(true);
    };

    window.addEventListener('openFeedbackWidget', handleOpenEvent);
    return () => {
      window.removeEventListener('openFeedbackWidget', handleOpenEvent);
    };
  }, []);
  const handleClose = () => {
    setOpen(false);
    // Reset form after closing
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        category: '',
        rating: 0,
        message: ''
      });
      setErrors({});
    }, 300);
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleCategorySelect = (category) => {
    setFormData({ ...formData, category });
    if (errors.category) {
      setErrors({ ...errors, category: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.category) newErrors.category = 'Please select a category';
    if (formData.rating === 0) newErrors.rating = 'Please rate your experience';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    
    // Log feedback submission
    logDev('info', 'Feedback submitted', formData);
    
    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setShowSuccess(true);
      handleClose();
    }, 1000);
  };

  return (
    <>
      {/* Floating Feedback Button */}
      <Fab
        color="primary"
        aria-label="Give feedback"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          backgroundColor: jamaicanColors.green,
          color: '#ffffff',
          zIndex: 1300,
          '&:hover': {
            backgroundColor: jamaicanColors.darkGreen,
            transform: 'scale(1.05)'
          }
        }}
      >
        <FeedbackIcon />
      </Fab>

      {/* Feedback Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: jamaicanColors.gold,
            color: jamaicanColors.black,
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FeedbackIcon />
            <Typography variant="h6" fontWeight={700}>
              Help Shape Jamaica's Job Platform
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: jamaicanColors.black,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your feedback helps us build a better platform for all Jamaicans, creating meaningful employment opportunities across the island.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Name Field */}
            <TextField
              fullWidth
              label="Your Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              variant="outlined"
            />

            {/* Email Field */}
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              variant="outlined"
            />

            {/* Category Selection */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                What's your feedback about? *
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {categories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    onClick={() => handleCategorySelect(category)}
                    color={formData.category === category ? 'primary' : 'default'}
                    variant={formData.category === category ? 'filled' : 'outlined'}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: formData.category === category
                          ? jamaicanColors.darkGreen
                          : themeMode === 'light' 
                            ? 'rgba(0, 150, 57, 0.1)' 
                            : 'rgba(76, 175, 80, 0.1)'
                      }
                    }}
                  />
                ))}
              </Box>
              {errors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.category}
                </Typography>
              )}
            </Box>

            {/* Rating */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                How would you rate your experience? *
              </Typography>
              <Rating
                value={formData.rating}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, rating: newValue });
                  if (errors.rating) {
                    setErrors({ ...errors, rating: '' });
                  }
                }}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: jamaicanColors.gold
                  }
                }}
              />
              {errors.rating && (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                  {errors.rating}
                </Typography>
              )}
            </Box>

            {/* Message Field */}
            <TextField
              fullWidth
              label="Your Message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleChange('message')}
              error={!!errors.message}
              helperText={errors.message}
              variant="outlined"
              placeholder="Share your thoughts, suggestions, or concerns..."
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="contained"
            endIcon={<SendIcon />}
            sx={{
              backgroundColor: jamaicanColors.green,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: jamaicanColors.darkGreen
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 150, 57, 0.5)'
              }
            }}
          >
            {submitting ? 'Sending...' : 'Send Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setShowSuccess(false)}
          sx={{
            backgroundColor: jamaicanColors.green,
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff'
            }
          }}
        >
          Thank you for your feedback! We'll review it carefully and use it to improve the platform.
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackWidget;
